import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap text-sm font-normal transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        // Dark charcoal filled
        default:
          "bg-primary text-primary-foreground rounded-lg hover:bg-primary-hover",
        // Destructive - only in dialogs
        destructive:
          "bg-destructive text-destructive-foreground rounded-lg hover:bg-destructive/90",
        // Outline - subtle border
        outline:
          "border border-border bg-card rounded-lg hover:bg-muted hover:text-foreground",
        // AI outline - iris text + iris border, for standalone / leading AI actions
        "ai-outline":
          "border border-[hsl(var(--ai-accent)/0.4)] bg-card text-[hsl(var(--ai-accent-text))] rounded-lg hover:bg-[hsl(var(--ai-accent-soft))] hover:border-[hsl(var(--ai-accent)/0.6)] hover:text-[hsl(var(--ai-accent-text))]",
        // AI ghost - iris text, no border, for AI actions sitting inside a ghost group
        "ai-ghost":
          "text-[hsl(var(--ai-accent-text))] rounded-lg hover:bg-[hsl(var(--ai-accent-soft))] hover:text-[hsl(var(--ai-accent-text))]",
        // Secondary - muted background
        secondary:
          "bg-muted text-foreground rounded-lg hover:bg-muted/80",
        // Ghost - minimal, rounded for icon containers
        ghost: "rounded-lg hover:bg-muted hover:text-foreground",
        // Link - blue underline
        link: "text-[hsl(var(--link))] underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2",     // Slightly taller
        sm: "h-8 px-3 text-xs",
        lg: "h-11 px-8",              // Larger for CTAs
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, ...props }, ref) => {
    return (
      <button
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
